import { AcsesCommunication } from "./AcsesCommunication";
import { AppointmentLog } from "./AppointmentLog";
import { AppointmentNote } from "./AppointmentNote";
import { AppointmentStatusHistory, AppointmentStatusID, STATUS_IDENTIFIERS } from "./AppointmentStatus";
import { Car, CAR_TYPE, CarID } from "./Car";
import { CarCheck, CarCheckID, DBB_STATUSES } from "./CarCheck";
import { CarCheckEvent } from "./CarCheckEvent";
import { CheckInRemark, CheckInResult } from "./CheckIn";
import { Checklist, ChecklistID } from "./Checklist";
import { Customer, CustomerID } from "./Customer";
import { COMMUNICATION_STATUSES, CommunicationResult, CustomerCommunication, CustomerCommunicationID, DiagnoseOverviewRemark } from "./CustomerCommunication";
import { CommunicationEvent } from "./CustomerCommunicationEvent";
import { DealerLocation, DealerLocationID } from "./DealerLocation";
import { DeskCommunication } from "./DeskCheckin";
import { Intervention } from "./Intervention";
import { KeyLockerCommunication } from "./KeylockerCommunication";
import { KioskLabelNumber } from "./Kiosk";
import { KioskCommunication } from "./KioskCommunication";
import { DMSUser, User, UserID } from "./User";

export type AppointmentID = number;

export enum STATUS_OVERRIDE_TYPES {
  None = 0,
  Canceled = 1,
  Restored = 2
}

export enum APPOINTMENT_RECALL_STATUSES {
  None = 0,
  Previous = 1,
  Open = 2
}

export enum CANCELLATION_REASONS {
  CanceledInDMS = 0,
  CustomerNoShow = 1,
  CustomerCancellation = 2,
  DealerCancellation = 3
}

export interface Appointment {
  created_on: string;
  updated_on: string;
  id: AppointmentID; // Appointment's ID
  dms_nr: string; // Appointment ID in the DMS
  dms_created_at: string; // When the Appointment was created in the DMS
  wo_nr: string; // Appointment work order number
  importer_version: string; // Version of Claire importer used to import Appointment from DMS
  car_check_started: boolean; // Car check has started
  customer_waiting: boolean; // The customer is waiting for the car
  customer_waiting_edited: boolean; // The customer is waiting for the car is set by the user
  is_local: boolean; // This appointment was created manually in Claire
  is_money: boolean; // A receptionist marked this appointment with the money marker
  is_star: boolean; // A receptionist marked this appointment with the star marker
  is_star_color: string; // Color for the star icon
  is_shop: boolean; // A receptionist marked this appointment with the shop marker
  is_shop_color: string; // Color for the shopping cart icon
  is_lease_company: boolean; // The appointment’s car belongs to a lease company
  has_extra_check: boolean; // An extra check was performed
  car_check_paused_at: string | null; // A car check was paused and when
  final_check_has_remarks: boolean; // Final check has remarks
  is_recurring: boolean; // This car came in the last 28 days
  warranty_pin_count: number; // Number of pinned warranty items on the appointment
  warranty_pin_signature_missing_count: number; // Number of pinned warranty items on the appointment that are missing a signature
  warranty_pin_claim_nr_missing_count: number; // Number of pinned warranty items on the appointment that are missing a claim nr
  warranty_pin_support_nr_missing_count: number; // Number of pinned warranty items on the appointment that are missing a claim nr
  recall_pin_count: number; // Number of pinned recall items on the appointment
  recall_pin_claim_nr_missing_count: number; // Number of pinned recall items on the appointment that are missing a claim nr
  recall_pin_support_nr_missing_count: number; // Number of pinned recall items on the appointment that are missing a supportnr
  remark_pin_count: number; // Number of pinned remark items on the appointment
  next_date: string | null; // Planned next appointment date
  predicted_next_date: string | null; // Predicted next appointment date
  time_car_app: string; // Appointment date in Claire
  is_pinned: boolean; // Is the appointment pinned
  is_pinned_manually: boolean; // The appointment was pinned manually, preventing automatic overwriting
  status_override: STATUS_OVERRIDE_TYPES; // The appointment's status was overridden manually, preventing automatic overwriting
  car_in_shop: string | null; // When the car arrived at the location
  car_in_shop_edited: boolean; // Car in Shop was overwritten in claire and no longer reflects the DMS
  car_out_of_shop: string | null; //  When the car left the location
  car_out_of_shop_edited: boolean; // Car out of Shop was overwritten in claire and no longer reflects the DMS
  due_in: string | null; // Car is scheduled to be in the shop on this date
  car_return_time: string | null; // Car is scheduled to be returned back to the customer on this date
  car_return_time_edited: boolean; // car return time was overwritten in claire and no longer reflects the DMS
  dms_billed_updated: boolean; // Billed was updated by the DMS
  check_in_com_sent: boolean; // Online Checkin was sent to a customer
  check_in_com_answered: boolean; // Online Checkin was answered by a customer
  check_in_com_to_handle: number; // The customer asked for additional items to be handled
  last_dms_update: string | null; // The last time this appointment was updated from the DMS
  last_dms_refresh: string; // The last time this appointment was refreshed by a user opening the detail page
  last_dbb_refresh: string; // The last time this appointment was refreshed to update data from DBB by a user opening the detail page
  last_ocw_refresh: string | null; // The last time the OCW was refreshed by a user opening the detail page
  last_recall_refresh: string; // The last time the recalls of this appointment was refreshed by a user opening the detail page
  internal: boolean; // This appointment is internal and doesn't interact with real customers, such as when a car is new from the factory
  has_panic: boolean; // A mechanic has a critical issue on the vehicle and needs immediate help
  is_tyre_motion_ai_scanned: boolean; // The tyres are scanned by tyre motion AI
  created_by: string; // Indicates the origin of this appointment and the entity responsible for its creation
  is_ocw_intervention_created: boolean; // Indicates if the OCW intervention has been created for this appointment. This intervention is created if the car supports OCW and it is reminder to disconnect and reconnect the car to ocw app.
  bar_code: string; // Custom BarCode, set while importing appointment via public-api. Contains additional information.
  three_department_receptionist: boolean; // Indicates if the receptionist department has been set for this appointment
  three_department_receptionist_set_at: string | null; // The time when the receptionist department was set for this appointment
  three_department_receptionist_user_id: UserID | null; // User ID who set the receptionist department
  three_department_parts: boolean; // Indicates if the parts department has been set for this appointment
  three_department_parts_set_at: string | null; // The time when the parts department was set for this appointment
  three_department_parts_user_id: UserID | null; // User ID who set the parts department
  three_department_workshop: boolean; // Indicates if the workshop department has been set for this appointment
  three_department_workshop_set_at: string | null; // The time when the workshop department was set for this appointment
  three_department_workshop_user_id: UserID | null; // User ID who set the workshop department
  three_department_remark: string; // Three department remark
  three_department_remark_user_id: UserID | null; // User ID who set the three department remark
  three_department_remark_updated_at: string | null; // The time when the three department remark was updated
  key_dropped_at: string | null; // Date and time the customer dropped the key in the keylocker
  key_dropped_in: string | null; // Name of the keylocker in which key is dropped in
  key_picked_up_at: string | null; // Date and time the mechanic picked up the key in the keylocker
  key_picked_up_by_id: UserID | null; // User ID of the mechanic who picked up the key
  key_dropped_back_at: string | null; // Date and time the mechanic dropped back the key in the keylocker
  key_dropped_back_by_id: UserID | null; // User ID of the mechanic who dropped back the key
  key_dropped_back_in: string | null; // Name of the keylocker in which key is dropped back in
  key_picked_up_back_at: string | null; // Date and time the customer picked back up the key in the keylocker
  key_picked_up_back_by_id: UserID | null; // User ID of the receptionist who picked back up the key if they intervened
  acses_key_drop_object_id: string | null; // Acses Object Identifier used to drop the key
  acses_key_drop_booking_id: string | null; // Acses Booking Identifier containing the authentication needed so the customer can drop the key
  acses_key_drop_in: string; // Identifier of the Acses locker, where the key was dropped in
  acses_key_dropped_at: string | null; // Date and time the customer dropped the key in the Acses locker
  acses_key_pick_up_booking_id: string | null; // Acses Booking Identifier used to open the locker to pickup the key
  acses_key_pick_up_by_id: UserID | null; // User Identifier of the mechanic who picked up the key from the Acses locker
  acses_key_picked_up_at: string | null; // Date and time the mechanic picked up the key from the Acses locker
  acses_key_drop_back_object_id: string | null; // Acses Object Identifier used to drop back the key
  acses_key_drop_back_booking_id: string | null; // Acses Booking Identifier containing the authentication needed so the receptionist can drop back the key
  acses_key_drop_back_by_id: UserID | null; // User Identifier of the receptionist who dropped back the key in the Acses locker
  acses_key_drop_back_in: string; // Identifier of the Acses locker, where the key was dropped back in
  acses_key_dropped_back_at: string | null; // Date and time the receptionist dropped the key in the Acses locker
  acses_key_pick_up_back_booking_id: string | null; // Acses Booking Identifier containing the authentication needed so the customer can pick back up the key
  acses_key_assist_pick_up_booking_id: string | null; // Acses Booking Identifier containing the authentication needed so the receptionist can pick back up the key
  acses_key_assist_pick_up_by_id: UserID | null; // User Identifier of the receptionist who picked back up the key if they intervened
  acses_key_picked_up_back_at: string | null; // Date and time the key was picked back up from the Acses locker
  sharebox_key_dropped_at: string | null; // Sharebox key dropped at
  sharebox_key_dropped_in_cabinet_id: number | null; // Sharebox Cabinet Identifier for Key drop
  sharebox_key_dropped_in_reservation_id: number | null; // ShareBox Reservation Identifier for Key drop
  sharebox_key_dropped_in_locker_no: number | null; // Sharebox Locker Identifier for Key drop
  sharebox_key_dropped_in_location: string | null; // Sharebox Location for Key drop
  sharebox_key_picked_up_at: string | null; // Sharebox key picked up at date and time
  sharebox_key_picked_up_by_id: UserID | null; // Sharebox key picked up by Claire User Identifier
  sharebox_key_dropped_back_at: string | null; // Sharebox key dropped back at date and time
  sharebox_key_dropped_back_by_id: UserID | null; // Sharebox key dropped back by Claire User Identifier
  sharebox_key_dropped_back_in_cabinet_id: number | null; // Sharebox Cabinet Identifier for Key drop back
  sharebox_key_dropped_back_in_receiver_id: number | null; // Sharebox Receiver Identifier used for key drop back and to be used for assist pickup
  sharebox_key_dropped_back_in_reservation_id: number | null; // Sharebox Reservation Identifier for Key drop back
  sharebox_key_dropped_back_in_locker_no: number | null; // Sharebox Locker Identifier for Key drop back
  sharebox_key_dropped_back_in_location: string | null; // Sharebox Location for Key drop back
  sharebox_key_picked_up_back_at: string | null; // Sharebox key picked back up at date and time
  sharebox_key_picked_up_back_by_id: UserID | null; // Sharebox Key picked back up by Claire User Identifier
  xentry_job_id: string; // Identifier of this appointment in XEntry
  kiosk_checked_in_at: string | null; // Customer used the Kiosk to checkin
  kiosk_label_number: KioskLabelNumber | null; // Customer used this Kiosk Label to checkin
  planning_mechanic_id: UserID | null; // A planning mechanic id from supported Planning interfaces
  planning_work_start: string | null; // Work start time
  planning_work_stop: string | null; // Work stop time
  has_plan_it_data: boolean; // There is plan-IT data available for this appointment
  has_recalls_data: APPOINTMENT_RECALL_STATUSES; // There are recalls available for this appointment
  has_ovis_data: boolean; // There are OVIS events available for this appointment
  customer_owner_name: string; // Name of the owner customer
  customer_owner_profile_picture: string; // Profile picture of the owner customer
  assigned_mechanic: UserID | null; // Assigned mechanic
  assigned_mechanic_order: number | null; // Assigned mechanic order
  last_assigned_mechanic: UserID | null; // Last assigned mechanic
  extra_parts_list_id: ChecklistID | null; // Checklist ID of the extra parts list, if an extra check was started on the appointment
  extra_car_check_id: CarCheckID | null; // Check ID of the extra check performed on the appointment
  appointment_status_id: AppointmentStatusID; // Current appointment's status ID in Claire
  customer_driver_id: CustomerID | null; // Customer (driver) ID in Claire
  customer_owner_id: CustomerID | null; // Customer (owner) ID in Claire
  customer_contract_id: CustomerID | null; // Customer (contractor) ID in Claire
  dealer_location_id: DealerLocationID; // Unique DealerLocation ID of the location that the appointment occurred in
  car_id: CarID; // Unique ID of the Car assigned to appointment
  vin_nr: string; // Vehicle Identification Number
  car_model: string; // Car Model
  car_make: string; // Car Make
  reg_number: string; // License plate number
  reg_number_escaped: string; // License plate number without dashes
  current_km: number; // Current odometer status
  dms_km: number; // Odometer reading from the DMS
  next_km: number; // Planned next appointment in kilometers on the odometer
  car_apk_date: string | null; // Dutch technical inspection DMS date
  car_hu_date: string | null; // German technical inspection DMS date
  driver_initials: string; // Driver initials
  driver_title: string; // Driver Title (e.g. Dhr. Mvr.,)
  driver_firstname: string; // Driver's first name
  driver_surname: string; // Driver's last name
  driver_opted_out_from_importing: boolean; // Driver opted out from importing
  contractor_initials: string; // Contractor's initials
  contractor_title: string; // Contractor's Title (e.g. Dhr. Mvr.,)
  contractor_firstname: string; // Contractor's first name
  contractor_surname: string; // Contractor's last name
  contractor_opted_out_from_importing: boolean; // Contractor opted out from importing
  owner_initials: string; // Owner's initials
  owner_title: string; // Owner's Title (e.g. Dhr. Mvr.,)
  owner_firstname: string; // Owner's first name
  owner_surname: string; // Owner's last name
  owner_opted_out_from_importing: boolean; // Owner opted out from importing
  company: string; // Contractor's company name
  appointment_status_identifier: STATUS_IDENTIFIERS; // Current appointment's status identifier in Claire
  last_timestamp: string | null; // Last time this appointment was updated
  last_user: string; // Name of the last user that interacted with the appointment
  last_user_profile_picture: string; // Profile picture of the last user that interacted with the appointment
  last_user_at: string | null; // Time at which the last user interacted with the appointment
  num_approved_items_not_fixed: number; // Number of items waiting to be fixed on the appointment
  has_dbb: boolean; // The appointment's car has DBB enabled
  dbb_appointment_date: string | null; // Date provided by DBB for the delivery to the location from the hotel
  car_check_dbb_status: DBB_STATUSES; // Status of pushing the car check to DBB
  car_check_dbb_status_reason: string; // Reason given by DBB for the car check status
  final_car_check_dbb_status: DBB_STATUSES; // Status of pushing the final car check to DBB
  final_car_check_dbb_status_reason: string; // Reason given by DBB for the final car check status
  dbb_file_reference: string; // File reference returned by DBB for the pushed appointment
  dbb_state: string; // Tyre state as last reported by DBB
  car_check_initiator_name: string; // Name of mechanic who initiated check
  car_check_initiator_email: string; // Email of mechanic who initiated check
  last_receptionist_name: string; // Name of receptionist who last updated the appointment
  last_receptionist_profile_picture: string; // Profile picture of receptionist who last updated the appointment
  last_customer_ok_set_by_id: UserID | null; // User who sets the appointment in customer ok status
  last_car_ready: string | null; // Updated whenever Car Ready status is set
  fleet_number: string; // Car fleet number
  vehicle_type: CAR_TYPE; // The vehicle's type
  customer_driver?: Customer; // Customer (Driver) in Claire
  customer_owner?: Customer; // Customer (Owner) in Claire
  customer_contract?: Customer; // Customer (Contract) in Claire
  check_initiator?: User; // User that initiated check
  dealer_location?: DealerLocation;
  car?: Car; // Car object, refer to that model for more details
  car_profile_picture?: string; // Car profile picture URL
  status_history?: AppointmentStatusHistory[]; // History of statuses
  interventions?: Intervention[]; // List of initial interventions
  car_checks?: CarCheck[];
  checklists?: Checklist[];
  customer_communication?: CustomerCommunication;
  acses_customer_communication: AcsesCommunication | null; // Embedded when Acses was used to exchange a key
  customcom_id?: CustomerCommunicationID;
  customcom_receptionist_key?: string;
  customcom_status?: COMMUNICATION_STATUSES;
  customcom_correct_phone?: string;
  customcom_correct_email?: string;
  customcom_customer_id?: CustomerID;
  customer_ok_user?: User;
  desk_communications?: DeskCommunication[];
  kiosk_communications?: KioskCommunication[];
  keylocker_communications?: KeyLockerCommunication[];
  diagnose_overview_results?: CommunicationResult[];
  repair_overview_results?: CommunicationResult[];
  communication_events?: CommunicationEvent[];
  check_in_remarks?: CheckInRemark[];
  check_in_results?: CheckInResult[];
  diagnose_overview_remarks?: DiagnoseOverviewRemark[];
  communication_note?: string;
  notes?: AppointmentNote[];
  dealer_name?: string;
  dealer_commercial_name?: string;
  location_name?: string;
  location_phone?: string;
  dealer_domain_name?: string;
  car_in_shop_set_by?: User;
  car_out_of_shop_set_by?: User;
  key_picked_up_by?: User; // Mechanic who picked up the key
  planning_mechanic?: User;
  key_dropped_back_by?: User;
  key_picked_up_back_by?: User;
  acses_key_pick_up_by?: User;
  acses_key_drop_back_by?: User;
  acses_key_assist_pick_up_by?: User;
  sharebox_key_picked_up_by?: User;
  sharebox_key_dropped_back_by?: User;
  sharebox_key_picked_up_back_by?: User;
  car_check_events?: CarCheckEvent[]; // Check events of the appointment, whether they are related to a status change in the status history or not
  logs?: AppointmentLog[]; // Logs of the appointment, like change in the customer owner etc.
  pdf_include_activity_logs?: boolean; // The multi check pdf needs to contain the activity logs
  pdf_include_customer_sign_logs?: boolean; // The multi check pdf needs to contain the customer sign logs
  three_department_receptionist_user?: User; // User who set the receptionist department
  three_department_parts_user?: User; // User who set the parts department
  three_department_workshop_user?: User; // User who set the workshop department
  three_department_remark_user?: User; // User who set or edited the three department remark
  dms_car_id?: string; // only used by dms v3 service
  dms_driver_id?: string; // only used by dms v3 service
  dms_owner_id?: string; // only used by dms v3 service
  dms_contractor_id?: string; // only used by dms v3 service
  dms_rental?: boolean; // only used by dms v3 service
  is_car_in_shop?: boolean; // set for the importer service only by dms v3 service
  extracted_dms_users?: DMSUser[]; // only used by dms v3 service
}

export interface CancellationReason {
  id: CANCELLATION_REASONS;
  name: string;
}
